import { useCallback, useEffect, useRef, useState } from 'react';

import { dispatchSnackbarWarningEvent } from '@dreryk/react-components';

import { ElevenLabsTranscriptionConfig } from '../transcription/ElevenLabsTranscriptionConfig';
import { useElevenLabsTranscription } from '../transcription/useElevenLabsTranscription';
import { useRedaction } from '../transcription/useRedaction';
import { translationKeys } from '../translations';
import type { Field, RedactionScope } from '../types';

import { htmlToPlainText, plainTextToHtml, replaceParagraphRange } from './visitFieldHtmlUtils';
import { useVisitFieldSelection } from './useVisitFieldSelection';

export type RedactSource = 'visit-field' | 'transcript';

export type UseDictationPanelResult = {
   transcription: ReturnType<typeof useElevenLabsTranscription>;
   redaction: ReturnType<typeof useRedaction>;
   textareaRef: React.RefObject<HTMLTextAreaElement>;
   partialText: string;
   finalText: string;
   selectedField: Field;
   redactSource: RedactSource;
   redactingField: Field | null;
   silenceSecondsRemaining: number | null;
   setFinalText: (text: string) => void;
   setSelectedField: (field: Field) => void;
   setRedactSource: (source: RedactSource) => void;
   isRecording: boolean;
   isConnecting: boolean;
   handleAccept: (onAppend: (field: Field, text: string) => void) => void;
   handleReject: VoidFunction;
};

// eslint-disable-next-line max-lines-per-function,max-statements
export const useDictationPanel = (
   config: ElevenLabsTranscriptionConfig,
   onRecordingChange: (isRecording: boolean) => void,
   getVisitFieldHtml: (field: Field) => string,
   setVisitFieldHtml: (field: Field, html: string) => void
   // eslint-disable-next-line max-params
): UseDictationPanelResult => {
   const [partialText, setPartialText] = useState('');
   const [finalText, setFinalText] = useState('');
   const [selectedField, setSelectedField] = useState<Field>('INTERVIEW');
   const [redactSource, setRedactSource] = useState<RedactSource>('visit-field');
   const [redactingField, setRedactingField] = useState<Field | null>(null);
   const [silenceSecondsRemaining, setSilenceSecondsRemaining] = useState<number | null>(null);

   // Stable refs for values that callbacks need without closing over stale renders
   const finalTextRef = useRef(finalText);
   finalTextRef.current = finalText;
   const partialTextRef = useRef(partialText);
   partialTextRef.current = partialText;
   const selectedFieldRef = useRef(selectedField);
   selectedFieldRef.current = selectedField;
   const redactSourceRef = useRef(redactSource);
   redactSourceRef.current = redactSource;

   const textareaRef = useRef<HTMLTextAreaElement>(null);

   // ─── Transcription callbacks ────────────────────────────────────────────

   const handlePartial = useCallback((text: string) => {
      setPartialText(text);
   }, []);

   const handleFinal = useCallback((text: string) => {
      setPartialText('');
      setFinalText((prev) => `${prev + text} `);
   }, []);

   const handleStop = useCallback(() => {
      setSilenceSecondsRemaining(null);
      // Read via ref — always current, no stale closure, no nested setState.
      const pending = partialTextRef.current;
      if (pending.trim().length > 0) {
         setFinalText((prev) => `${prev + pending} `);
      }
      setPartialText('');
   }, []);

   const transcriptionOptions = {
      onPartial: handlePartial,
      onFinal: handleFinal,
      onStop: handleStop,
      onSilenceSeconds: useCallback((seconds: number) => setSilenceSecondsRemaining(seconds), []),
      onSpeaking: useCallback(() => setSilenceSecondsRemaining(null), []),
      onSilenceDisconnect: useCallback(() => {
         setSilenceSecondsRemaining(null);
         dispatchSnackbarWarningEvent(translationKeys.faults.microphoneSilenceDisconnect);
      }, []),
   };

   const transcription = useElevenLabsTranscription(transcriptionOptions);

   // ─── Redaction — transcript source ──────────────────────────────────────
   const getTranscriptText = useCallback((): string => finalTextRef.current, []);

   const getTranscriptSelectionRange = useCallback((): {
      start: number;
      end: number;
      text: string;
   } | null => {
      const el = textareaRef.current;
      if (!el) {
         return null;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd;
      if (start === end) {
         return null;
      }
      return { start: start, end: end, text: el.value.slice(start, end) };
   }, []);

   const handleTranscriptRedactionAccept = useCallback((redactedText: string, scope: RedactionScope) => {
      if (scope.kind === 'selection') {
         setFinalText((prev) => prev.slice(0, scope.selectionStart) + redactedText + prev.slice(scope.selectionEnd));
      } else {
         setFinalText(redactedText);
      }
   }, []);

   // ─── Redaction — visit-field source ─────────────────────────────────────

   const onReplaceField = useCallback(
      (field: Field, redactedText: string) => {
         setVisitFieldHtml(field, plainTextToHtml(redactedText));
      },
      [setVisitFieldHtml]
   );

   const onReplaceSelectionInField = useCallback(
      (field: Field, startIndex: number, endIndex: number, redactedText: string) => {
         const currentHtml = getVisitFieldHtml(field);
         const splicedHtml = replaceParagraphRange(currentHtml, startIndex, endIndex, plainTextToHtml(redactedText));
         setVisitFieldHtml(field, splicedHtml ?? plainTextToHtml(redactedText));
      },
      [getVisitFieldHtml, setVisitFieldHtml]
   );

   const visitFieldSelection = useVisitFieldSelection({
      getVisitFieldText: useCallback((field: Field) => htmlToPlainText(getVisitFieldHtml(field)), [getVisitFieldHtml]),
      selectedField: useCallback(() => selectedFieldRef.current, []),
      onReplaceField: onReplaceField,
      onReplaceSelectionInField: onReplaceSelectionInField,
   });

   // ─── Unified redaction ───────────────────────────────────────────────────

   const getFieldText = useCallback((): string => {
      if (redactSourceRef.current === 'transcript') {
         return getTranscriptText();
      }
      return visitFieldSelection.getFieldText();
   }, [getTranscriptText, visitFieldSelection]);

   const getSelectionRange = useCallback((): {
      start: number;
      end: number;
      text: string;
   } | null => {
      if (redactSourceRef.current === 'visit-field') {
         const range = visitFieldSelection.getSelectionRange();
         const detectedField = visitFieldSelection.getDetectedField();
         if (detectedField) {
            selectedFieldRef.current = detectedField;
         }
         setRedactingField(selectedFieldRef.current);
         return range;
      }
      return getTranscriptSelectionRange();
   }, [visitFieldSelection, getTranscriptSelectionRange]);

   const handleRedactionAccept = useCallback(
      (redactedText: string, scope: RedactionScope) => {
         if (redactSourceRef.current === 'visit-field') {
            visitFieldSelection.handleRedactionAccept(redactedText, scope);
         } else {
            handleTranscriptRedactionAccept(redactedText, scope);
         }
      },
      [visitFieldSelection, handleTranscriptRedactionAccept]
   );

   const redaction = useRedaction({
      field: () => selectedFieldRef.current,
      getFieldText: getFieldText,
      getSelectionRange: getSelectionRange,
      onAccept: handleRedactionAccept,
      onEmpty: useCallback(() => {
         setRedactingField(null);
         dispatchSnackbarWarningEvent(translationKeys.dictationMode.redactEmptyWarning);
      }, []),
   });

   // ─── Accept / reject transcript ──────────────────────────────────────────

   const handleAccept = useCallback((onAppend: (field: Field, text: string) => void) => {
      const text = finalTextRef.current.trim();
      if (text) {
         onAppend(selectedFieldRef.current, text);
      }
      setFinalText('');
      setPartialText('');
   }, []);

   const handleReject = useCallback(() => {
      setFinalText('');
      setPartialText('');
   }, []);

   // ─── Derived state ───────────────────────────────────────────────────────

   const isRecording = transcription.state === 'recording';
   const isConnecting = transcription.state === 'requesting-credentials' || transcription.state === 'connecting';

   /*
    * Track which field is being redacted. Captures selectedFieldRef at redaction start
    * so that the visit form can disable the correct editor.
    */
   useEffect(() => {
      const isRedacting = redaction.redactionState !== 'idle' && redaction.redactionState !== 'error';
      if (!isRedacting) {
         setRedactingField(null);
      }
   }, [redaction.redactionState]);

   /*
    * Reset redact source to 'visit-field' when the transcript is cleared (accept/reject/stop).
    * Prevents the toggle staying on 'transcript' with nothing to redact.
    */
   useEffect(() => {
      if (finalText.trim().length === 0) {
         setRedactSource('visit-field');
      }
   }, [finalText]);

   /*
    * Drive onRecordingChange from state — fires even when the session ends
    * due to a WS timeout or unmount, not just on user-click.
    */
   const onRecordingChangeRef = useRef(onRecordingChange);
   onRecordingChangeRef.current = onRecordingChange;

   useEffect(() => {
      onRecordingChangeRef.current(isRecording);
   }, [isRecording]);

   return {
      transcription: transcription,
      redaction: redaction,
      textareaRef: textareaRef,
      partialText: partialText,
      finalText: finalText,
      selectedField: selectedField,
      redactSource: redactSource,
      redactingField: redactingField,
      silenceSecondsRemaining: silenceSecondsRemaining,
      setFinalText: setFinalText,
      setSelectedField: setSelectedField,
      setRedactSource: setRedactSource,
      isRecording: isRecording,
      isConnecting: isConnecting,
      handleAccept: handleAccept,
      handleReject: handleReject,
   };
};
